import type { ParsedInventoryRow } from './parseInventoryCsv';

/**
 * The slice of a current inventory row the diff actually reads. Kept
 * minimal so the caller can feed the raw full-pool snapshot without
 * materializing full view rows; `InventoryRow` is structurally
 * assignable.
 */
export interface SnapshotInventoryRow {
    id: string;
    symbol: string;
    currentQuantity: number;
    currentLabPrice: number;
}

export interface UploadAddedItem {
    symbol: string;
    quantity: number;
    /**
     * `null` when the upload CSV had no `LAB Price` column. The wizard
     * must surface this as missing — applying a silent `$0` for new
     * inventory is a financial-data integrity hazard.
     */
    labPrice: number | null;
}

export interface UploadUpdatedItem {
    rowId: string;
    symbol: string;
    fromQuantity: number;
    toQuantity: number;
    quantityChanged: boolean;
    fromLabPrice: number;
    toLabPrice: number;
    labPriceChanged: boolean;
}

export interface UploadRemovedItem {
    rowId: string;
    symbol: string;
    currentQuantity: number;
    currentLabPrice: number;
}

export interface UploadUnchangedItem {
    rowId: string;
    symbol: string;
}

export interface UploadDiff {
    added: ReadonlyArray<UploadAddedItem>;
    updated: ReadonlyArray<UploadUpdatedItem>;
    removed: ReadonlyArray<UploadRemovedItem>;
    unchanged: ReadonlyArray<UploadUnchangedItem>;
}

const PRICE_EPSILON = 1e-6;

function priceEqual(a: number, b: number): boolean {
    return Math.abs(a - b) < PRICE_EPSILON;
}

/**
 * Categorize a parsed CSV against the current pool snapshot.
 *
 * The upload is treated as a full sync of the pool: parsed rows present
 * in the snapshot with the same values are *unchanged*; parsed rows present
 * with different values are *updated*; parsed rows absent from the snapshot
 * are *added*; snapshot rows absent from the parsed file are *removed*.
 *
 * `labPrice` of `null` from the parser means "no LAB Price column in the
 * upload" — the existing snapshot price is retained for unchanged/updated.
 */
export function diffInventory(
    parsedRows: ReadonlyArray<ParsedInventoryRow>,
    currentRows: ReadonlyArray<SnapshotInventoryRow>,
): UploadDiff {
    const currentBySymbol = new Map<string, SnapshotInventoryRow>();
    for (const row of currentRows) currentBySymbol.set(row.symbol, row);

    const seenSymbols = new Set<string>();
    const added: UploadAddedItem[] = [];
    const updated: UploadUpdatedItem[] = [];
    const unchanged: UploadUnchangedItem[] = [];

    for (const parsed of parsedRows) {
        seenSymbols.add(parsed.symbol);
        const current = currentBySymbol.get(parsed.symbol);
        const nextPrice = parsed.labPrice;

        if (!current) {
            // Preserve the upload's `null` price intact — adding a brand-new
            // symbol with no price is something the wizard must flag, not
            // silently coerce to `$0`.
            added.push({
                symbol: parsed.symbol,
                quantity: parsed.quantity,
                labPrice: nextPrice,
            });
            continue;
        }

        const effectivePrice = nextPrice ?? current.currentLabPrice;
        const quantityChanged = current.currentQuantity !== parsed.quantity;
        const labPriceChanged = nextPrice !== null && !priceEqual(current.currentLabPrice, effectivePrice);

        if (!quantityChanged && !labPriceChanged) {
            unchanged.push({ rowId: current.id, symbol: current.symbol });
            continue;
        }

        updated.push({
            rowId: current.id,
            symbol: current.symbol,
            fromQuantity: current.currentQuantity,
            toQuantity: parsed.quantity,
            quantityChanged,
            fromLabPrice: current.currentLabPrice,
            toLabPrice: effectivePrice,
            labPriceChanged,
        });
    }

    const removed: UploadRemovedItem[] = [];
    for (const row of currentRows) {
        if (seenSymbols.has(row.symbol)) continue;
        removed.push({
            rowId: row.id,
            symbol: row.symbol,
            currentQuantity: row.currentQuantity,
            currentLabPrice: row.currentLabPrice,
        });
    }

    return { added, updated, removed, unchanged };
}
